import React, { useEffect, useMemo } from 'react'
import PartySelectionCard, { fmtMoney } from '../PartySelectionCard'
import ClientInformation from '../../HourlyInvoice/ClientInformation'
import { useHourlyUpdateInvoice } from './context/HourlyUpdateInvoiceContext'
import { partyLabelFor, partyRecordOf, partyRoleFor } from './partyLabel'
import { ledgerFor } from './similarity'

/**
 * Step 1 — which invoices this edit rewrites, then check the bill-to details.
 *
 * CHROME IS CM'S, NOT SCALE'S. The heading, the selection cards and the
 * bill-to cards are the same ones the FIXED editor
 * (UpdateInvoice/Step1SelectParty) and the hourly CREATE flow
 * (HourlyInvoice/InvoiceFirstStep) already use, so the three screens are
 * indistinguishable.
 *
 * WHERE IT NECESSARILY DIFFERS FROM THE FIXED EDITOR
 * Fixed offers exactly three choices (plaintiff / defendant / both) because a
 * fixed case has at most one invoice per side. An hourly case routinely has
 * more — over half carry several, one carries eight — so the party choice is a
 * list of INVOICES, and the combined option is gated on those invoices being
 * one bill rather than always available.
 */

/** Bill-to card for one invoice. */
const PartyPanel = ({ row }) => {
    const { clientInfoByInvoice, setClientInfoFor, caseInfo, mediatorRecord } =
        useHourlyUpdateInvoice()

    /* Derived SYNCHRONOUSLY. An effect-seeded prop would make the first render
     * of every card blank, and N cards give that trap N chances. */
    const stored = clientInfoByInvoice[row.id]
    const seeded = useMemo(() => {
        if (stored) return stored
        const party = partyRecordOf(row, caseInfo)
        const fallback = row.clients || {}
        return {
            client_information: {
                client_id: row.client_id,
                name: party?.name || fallback.name || '',
                email: party?.email || fallback.email || '',
                phone_number:
                    party?.phone || party?.phone_number || fallback.phone_number || '',
                address: party?.address || fallback.address || '',
            },
            invoice_emails: '',
        }
    }, [stored, row, caseInfo])

    useEffect(() => {
        if (row && !clientInfoByInvoice[row.id]) setClientInfoFor(row.id, seeded)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [row?.id, seeded])

    /* CM's ClientInformation CONSUMES a flat client but EMITS the nested
     * `{ client_information: {…} }` shape the update endpoint wants. Adapting
     * only one direction is why a bill-to card blanks out after its first save. */
    const flatClient = {
        client_id: seeded.client_information?.client_id ?? row.client_id,
        name: seeded.client_information?.name || '',
        email: seeded.client_information?.email || '',
        phone: seeded.client_information?.phone_number || '',
        address: seeded.client_information?.address || '',
        invoice_emails: seeded.invoice_emails
            ? String(seeded.invoice_emails)
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : [],
    }

    return (
        <ClientInformation
            client={flatClient}
            label={partyRoleFor(row, caseInfo, mediatorRecord)}
            onClientChange={(_clientId, patch) => {
                const ci = patch?.client_information || {}
                setClientInfoFor(row.id, {
                    client_information: {
                        ...seeded.client_information,
                        ...(ci.name !== undefined ? { name: ci.name } : {}),
                        ...(ci.email !== undefined ? { email: ci.email } : {}),
                        ...(ci.phone_number !== undefined
                            ? { phone_number: ci.phone_number }
                            : {}),
                        ...(ci.address !== undefined ? { address: ci.address } : {}),
                    },
                    invoice_emails:
                        patch?.invoice_emails !== undefined
                            ? patch.invoice_emails
                            : seeded.invoice_emails,
                })
            }}
        />
    )
}

export default function Step1() {
    const {
        invoiceRows,
        candidateRows,
        unresolvedRows,
        combinedGate,
        selectedInvoiceIds,
        selectionMode,
        setSelection,
        targetRows,
        caseInfo,
        mediatorRecord,
        groups,
        selectedGroup,
        selectedGroupKey,
        setSelectedGroupKey,
        sharedSiblingCountFor,
        blockedReasonFor,
        notify,
    } = useHourlyUpdateInvoice()

    /* One editable invoice is not a choice — select it so the case manager
     * lands on the bill-to card rather than an empty step. Declared before the
     * early returns: hooks cannot sit behind a condition. */
    useEffect(() => {
        if (candidateRows.length === 1 && selectedInvoiceIds.length === 0) {
            setSelection([String(candidateRows[0].id)], 'one')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateRows, selectedInvoiceIds])

    if (invoiceRows.length === 0) {
        return (
            <div className="flex flex-col items-center text-center px-2 py-10">
                <div className="text-base font-semibold text-gray-900 mb-1.5">
                    No hourly invoices
                </div>
                <p className="text-sm text-gray-500 leading-relaxed max-w-sm">
                    This case has no hourly invoices to edit yet.
                </p>
            </div>
        )
    }

    /* NO EARLY RETURN WHEN THERE IS NOTHING TO SELECT.
     *
     * Returning here used to skip the billing-run picker AND the unresolved-rows
     * notice below, both of which are the only way out of this state:
     *
     *  - the picker, because "no editable rows" is scoped to ONE run; another
     *    run on the same case may be fully editable;
     *  - the notice, because `candidateRows` drops every row whose side `sideOf`
     *    cannot resolve, and on a GSC case (cases.plaintiff_id / defender_id
     *    NULL, no case_parties rows) that is EVERY row. 71 UAT cases are in that
     *    shape, each holding a PENDING hourly invoice — case 729 holds an
     *    outstanding $1,350. The early return told the case manager it was
     *    settled.
     *
     * So the shell renders as usual and only the party-selection block is
     * replaced, with copy branched on the actual cause. */
    const hasCandidates = candidateRows.length > 0

    /* "Already settled" is claimed ONLY when nothing editable is left. When the
     * pool still holds editable rows that could not be attributed to a side,
     * say that instead — it is the true reason and it is actionable. */
    const emptyState = hasCandidates
        ? null
        : unresolvedRows.length > 0
          ? {
                title: 'These invoices are not linked to a party',
                body: `${unresolvedRows.length} outstanding invoice${
                    unresolvedRows.length === 1 ? '' : 's'
                } on this case could not be matched to a plaintiff or defendant, so ${
                    unresolvedRows.length === 1 ? 'it' : 'they'
                } cannot be edited here. Check the parties on the case first.`,
            }
          : {
                title: 'Nothing left to edit',
                body: 'Every hourly invoice on this case is already settled. To bill more, create a new invoice from the case.',
            }

    const allIds = candidateRows.map((r) => String(r.id))
    const isAll = selectionMode === 'all'
    const isOne = selectionMode === 'one'

    /* Labelled "now" because r.amount is the CURRENT billed figure, not what
     * the edit will produce — three screens showing three unlabelled numbers is
     * what reads as "the totals do not match". */
    const partyOptions = candidateRows.map((r) => ({
        value: String(r.id),
        label: `${partyLabelFor(r, caseInfo, mediatorRecord)} — now ${fmtMoney(r.amount)}`,
    }))

    const blockedRows = (selectedGroup ? selectedGroup.rows : invoiceRows).filter((r) =>
        blockedReasonFor(r)
    )

    /* The single selected row, and how many other invoices share its session. */
    const scopeRow = targetRows.length === 1 ? targetRows[0] : null
    const scopeSiblings = scopeRow ? sharedSiblingCountFor(scopeRow) : 0

    const plaintiffTargets = targetRows.filter((r) => r.side === 'plaintiff')
    const defendantTargets = targetRows.filter((r) => r.side === 'defendant')

    const runLabel = (g) =>
        `${
            g.created_at
                ? new Date(g.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                  })
                : 'Invoice run'
        } — ${g.rows.length} invoice${g.rows.length === 1 ? '' : 's'} · ${fmtMoney(g.total)}`

    const headerDescription = !hasCandidates
        ? 'There is nothing on this billing run that can be edited here.'
        : blockedRows.length > 0
          ? 'Some invoices on this case are already paid and cannot be changed.'
          : 'Choose whose invoice this edit rewrites.'

    /* The one-party card carries a party picker inside it, so it cannot be the
     * <button>-based PartySelectionCard (a <select> may not be nested inside a
     * button). This is the same div-card the hourly CREATE flow uses for
     * exactly this pattern — identical gradient, radio dot and type scale. */
    const oneCardCls = `border rounded-lg p-4 cursor-pointer transition-colors ${
        isOne ? 'border-transparent text-white shadow-sm' : 'border-gray-200 hover:border-gray-300 bg-white'
    }`
    const oneCardStyle = isOne
        ? { background: 'linear-gradient(90deg, #115E59 0%, #0F766E 100%)' }
        : {}

    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-base font-semibold text-gray-900">Select Party</h3>
                <p className="text-sm text-gray-500 mt-0.5">{headerDescription}</p>
            </div>

            {/* Said HERE rather than after the whole wizard has been filled in.
                Reissuing means voiding a Stripe invoice and creating a new one,
                so a mediator with no connected Stripe account is refused by the
                server — this only warns, it does not gate: the server stays the
                authority on whether the edit can proceed. */}
            {mediatorRecord && !mediatorRecord.stripe_session && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="text-xs text-gray-700 leading-relaxed">
                        This mediator is not connected to Stripe, so the invoice
                        service will refuse to reissue. Connect their Stripe account
                        before editing.
                    </p>
                </div>
            )}

            {groups.length > 1 && (
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                        Which billing run?
                    </label>
                    <select
                        value={selectedGroupKey || groups[0].key}
                        onChange={(e) => setSelectedGroupKey(e.target.value)}
                        className="w-full text-sm border border-gray-300 bg-white text-gray-900 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-700/30"
                    >
                        {groups.map((g) => (
                            <option key={g.key} value={g.key}>
                                {runLabel(g)}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {emptyState && (
                <div className="flex flex-col items-center text-center px-2 py-10">
                    <div className="text-base font-semibold text-gray-900 mb-1.5">
                        {emptyState.title}
                    </div>
                    <p className="text-sm text-gray-500 leading-relaxed max-w-sm">
                        {emptyState.body}
                    </p>
                </div>
            )}

            {hasCandidates && (
            <div className="space-y-3">
                {/* Offered only when there is more than one invoice to combine —
                    "all 1 parties" is a question with one answer. */}
                {candidateRows.length > 1 && (
                    <PartySelectionCard
                        label="Update all parties"
                        subtext={
                            combinedGate.ok
                                ? `The revised invoice will be sent to all ${candidateRows.length} parties involved in the mediation`
                                : combinedGate.reason
                        }
                        selected={isAll}
                        disabled={!combinedGate.ok}
                        disabledReason={combinedGate.reason}
                        onSelect={() => setSelection(allIds, 'all')}
                        onClickWhenDisabled={() =>
                            notify?.('warning', combinedGate.reason)
                        }
                    />
                )}

                <div
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                        setSelection(
                            selectedInvoiceIds.length === 1 ? selectedInvoiceIds : [],
                            'one'
                        )
                    }
                    className={oneCardCls}
                    style={oneCardStyle}
                >
                    <div className="flex items-start gap-3">
                        <span
                            className={`mt-0.5 w-4 h-4 rounded-full border ${
                                isOne ? 'border-white' : 'border-gray-400'
                            } flex items-center justify-center shrink-0`}
                        >
                            {isOne ? <span className="w-2 h-2 rounded-full bg-white" /> : null}
                        </span>
                        <div className="flex-1 min-w-0">
                            <div
                                className={`font-bold text-[15px] ${
                                    isOne ? 'text-white' : 'text-gray-900'
                                }`}
                            >
                                Update one party
                            </div>
                            <div
                                className={`text-[13px] mt-0.5 ${
                                    isOne ? 'text-white/90' : 'text-gray-500'
                                }`}
                            >
                                Select a specific party to receive the revised invoice
                            </div>
                            {isOne && (
                                <select
                                    value={selectedInvoiceIds[0] || ''}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) =>
                                        setSelection(
                                            e.target.value ? [e.target.value] : [],
                                            'one'
                                        )
                                    }
                                    className="mt-2 w-full text-sm border border-gray-300 bg-white text-gray-900 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-700/30"
                                >
                                    <option value="">Please Select</option>
                                    {partyOptions.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* WHAT WILL HAPPEN, stated — not a second question to answer.
                Only shown when this invoice actually shares its stored session
                with others; about half of live rows do not, and for those there
                is no split to disclose. */}
            {isOne && scopeRow && scopeSiblings > 0 && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <p className="text-sm text-gray-700">
                        Editing{' '}
                        <span className="font-semibold">
                            {partyLabelFor(scopeRow, caseInfo, mediatorRecord)}
                        </span>
                        &rsquo;s own invoice. The other {scopeSiblings} part
                        {scopeSiblings === 1 ? 'y' : 'ies'} on this{' '}
                        {fmtMoney(ledgerFor(scopeRow).sum)} session {scopeSiblings === 1 ? 'is' : 'are'} not affected.
                    </p>
                    {combinedGate.ok ? (
                        <button
                            type="button"
                            onClick={() => setSelection(allIds, 'all')}
                            className="mt-1 text-xs font-medium text-teal-700 hover:text-teal-900"
                        >
                            The session itself ran differently — correct all{' '}
                            {scopeSiblings + 1} instead
                        </button>
                    ) : (
                        <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                            {combinedGate.reason}
                        </p>
                    )}
                </div>
            )}

            {/* Bill-to cards — the create flow's two-column layout. */}
            {targetRows.length > 0 && (
                <div className="grid grid-cols-12 gap-3">
                    {plaintiffTargets.length > 0 && (
                        <div className="col-span-12 md:col-span-6 space-y-2">
                            {plaintiffTargets.map((r) => (
                                <PartyPanel key={r.id} row={r} />
                            ))}
                        </div>
                    )}
                    {defendantTargets.length > 0 && (
                        <div className="col-span-12 md:col-span-6 space-y-2">
                            {defendantTargets.map((r) => (
                                <PartyPanel key={r.id} row={r} />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Why a party is missing. Without this the case manager sees fewer
                invoices than parties and cannot tell settled from broken. */}
            {blockedRows.length > 0 && (
                <p className="text-[13px] leading-relaxed text-gray-500">
                    {blockedRows
                        .map(
                            (r) =>
                                `${partyLabelFor(r, caseInfo, mediatorRecord)} — ${String(
                                    blockedReasonFor(r)
                                ).toLowerCase()}`
                        )
                        .join('; ')}
                    . These invoices cannot be edited.
                </p>
            )}

            {/* Suppressed when there are no candidates at all — the empty state
                above is already saying this, in full. */}
            {unresolvedRows.length > 0 && hasCandidates && (
                <p className="text-[13px] leading-relaxed text-gray-500">
                    {unresolvedRows.length} invoice
                    {unresolvedRows.length === 1 ? '' : 's'} on this case could not be
                    matched to a plaintiff or defendant and cannot be edited here.
                </p>
            )}
        </div>
    )
}
